import React, { useCallback, useEffect, useState } from "react";
import { Alert, Col, Form, FormFeedback, Input, Label, Row } from "reactstrap";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import PageLoader from "components/common/PageLoader";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import companyProfileSchema from "./schemas/company.schema";
import { getCompany, updateCompany } from "./services/SettingsService";
import type {
  CompanyFormValues,
  CompanyProfileRow,
} from "./types/settings.types";
import { companyToFormValues } from "./utils/settingsNormalize";
import { toastSettingsError } from "./utils/settingsToast";

const CompanyTab = () => {
  const { can } = useAccess();
  const canUpdate = can(Permissions.SETTINGS.UPDATE);
  const [loading, setLoading] = useState(true);
  const [companyRow, setCompanyRow] = useState<CompanyProfileRow | null>(null);

  const formik = useFormik<CompanyFormValues>({
    enableReinitialize: true,
    initialValues: companyToFormValues(companyRow),
    validationSchema: companyProfileSchema,
    onSubmit: async (values) => {
      if (!canUpdate) return;
      try {
        const res = await updateCompany({
          name: values.name.trim(),
          address: values.address.trim(),
          phone: values.phone.trim(),
          email: values.email.trim(),
          gstNumber: values.gstNumber.trim(),
        });
        if (res.success) {
          toast.success(res.message || "Company profile saved");
          const row = await getCompany();
          setCompanyRow(row);
        } else {
          toast.error(res.message || "Could not save company");
        }
      } catch (err) {
        toastSettingsError(err, "Could not save company");
      }
    },
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const row = await getCompany();
      setCompanyRow(row);
    } catch {
      toast.error("Could not load company profile");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return <PageLoader message="Loading company profile…" />;
  }

  return (
    <Form onSubmit={formik.handleSubmit}>
      <p className="text-muted small mb-3">
        Primary organization details. Saving creates the company record if none
        exists yet (required before bank or SMTP).
      </p>
      <Row className="mb-3">
        <Col lg={3}>
          <Label htmlFor="co-name" className="form-label">
            Name <span className="text-danger">*</span>
          </Label>
        </Col>
        <Col lg={9}>
          <Input
            id="co-name"
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={!!(formik.touched.name && formik.errors.name)}
            disabled={!canUpdate}
          />
          {formik.touched.name && formik.errors.name ? (
            <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
          ) : null}
        </Col>
      </Row>
      <Row className="mb-3">
        <Col lg={3}>
          <Label htmlFor="co-address" className="form-label">
            Address <span className="text-danger">*</span>
          </Label>
        </Col>
        <Col lg={9}>
          <Input
            id="co-address"
            name="address"
            type="textarea"
            rows={5}
            value={formik.values.address}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={!!(formik.touched.address && formik.errors.address)}
            disabled={!canUpdate}
          />
          {formik.touched.address && formik.errors.address ? (
            <FormFeedback type="invalid">{formik.errors.address}</FormFeedback>
          ) : null}
        </Col>
      </Row>
      <Row className="mb-3">
        <Col lg={3}>
          <Label htmlFor="co-phone" className="form-label">
            Phone <span className="text-danger">*</span>
          </Label>
        </Col>
        <Col lg={9}>
          <Input
            id="co-phone"
            name="phone"
            value={formik.values.phone}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={!!(formik.touched.phone && formik.errors.phone)}
            disabled={!canUpdate}
          />
          {formik.touched.phone && formik.errors.phone ? (
            <FormFeedback type="invalid">{formik.errors.phone}</FormFeedback>
          ) : null}
        </Col>
      </Row>
      <Row className="mb-3">
        <Col lg={3}>
          <Label htmlFor="co-email" className="form-label">
            Email <span className="text-danger">*</span>
          </Label>
        </Col>
        <Col lg={9}>
          <Input
            id="co-email"
            name="email"
            type="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={!!(formik.touched.email && formik.errors.email)}
            disabled={!canUpdate}
          />
          {formik.touched.email && formik.errors.email ? (
            <FormFeedback type="invalid">{formik.errors.email}</FormFeedback>
          ) : null}
        </Col>
      </Row>
      <Row className="mb-3">
        <Col lg={3}>
          <Label htmlFor="co-gst" className="form-label">
            GST number <span className="text-danger">*</span>
          </Label>
        </Col>
        <Col lg={9}>
          <Input
            id="co-gst"
            name="gstNumber"
            value={formik.values.gstNumber}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={!!(formik.touched.gstNumber && formik.errors.gstNumber)}
            disabled={!canUpdate}
          />
          {formik.touched.gstNumber && formik.errors.gstNumber ? (
            <FormFeedback type="invalid">
              {formik.errors.gstNumber}
            </FormFeedback>
          ) : null}
        </Col>
      </Row>
      {!canUpdate ? (
        <Alert color="light" className="border mb-3">
          You do not have permission to update settings.
        </Alert>
      ) : null}
      <div className="hstack gap-2 justify-content-end">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={!canUpdate || formik.isSubmitting}
        >
          {formik.isSubmitting ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              />
              Saving…
            </>
          ) : (
            "Save company"
          )}
        </button>
      </div>
    </Form>
  );
};

export default CompanyTab;
